import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { InfoPopover } from "../InfoPopover";

/**
 * Reusable KPI tile. A small card with a label, a big number, an optional
 * sub-line, and an optional info popover. The `tone` prop colors the value
 * by a semantic threshold judgement (positive / warning / danger) — leave
 * unset for neutral tiles where the number speaks for itself.
 */
export type KpiTone = "neutral" | "positive" | "warning" | "danger";

const TONE_CLASS: Record<KpiTone, string> = {
  neutral: "text-foreground",
  positive: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

export function KpiTile({
  label,
  value,
  sub,
  tone = "neutral",
  info,
  infoTitle,
  className,
  onClick,
  selected,
  progress,
  swatchClass,
  bare = false,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: KpiTone;
  info?: React.ReactNode;
  infoTitle?: string;
  className?: string;
  /** Makes the tile a toggle/filter button (hover lift + pointer). */
  onClick?: () => void;
  /** Highlights the tile while its filter is active (pair with onClick). */
  selected?: boolean;
  /** Bottom fill bar, e.g. attach rate. `barClass` is a bg-* utility. */
  progress?: { pct: number; barClass?: string };
  /** Small color swatch in the top-right corner (bg-* utility) — ties the
   *  tile to a same-colored series/column elsewhere on the page. */
  swatchClass?: string;
  /** Drop the Card chrome and render just the label + value, for a stat that
   *  lives INSIDE another card (a 3-up row on an item card, say). Without this
   *  the alternative is hand-rolling the stat, which is how tiles drift out of
   *  step with each other. `progress` and `onClick` don't apply. */
  bare?: boolean;
}) {
  // Meter tiles (those with a progress bar) render in rows and must line up
  // regardless of label/sub length: the card fills its grid cell, the label
  // reserves two lines so the value always starts at the same height, and the
  // bar is pinned to the bottom so every bar aligns across the row.
  const meter = progress != null;

  if (bare) {
    return (
      <div className={cn("flex flex-col gap-0.5 text-center", className)}>
        <div className={cn("text-xl font-semibold tabular-nums", TONE_CLASS[tone])}>
          {value}
        </div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    );
  }

  return (
    <Card
      className={cn(
        "flex h-full flex-col",
        onClick &&
          "cursor-pointer transition hover:-translate-y-0.5 hover:shadow-md",
        selected && "border-foreground",
        className,
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <CardHeader className="pb-1.5">
        <div
          className={cn(
            "flex justify-between gap-2",
            meter ? "items-start" : "items-center",
          )}
        >
          <div
            className={cn(
              "text-xs uppercase tracking-wider text-muted-foreground",
              // Two-line clamp keeps a long label (e.g. "Reputation, Surveys
              // & Reviews") from pushing the value down out of step with its
              // single-line neighbors.
              meter && "line-clamp-2 min-h-[2rem]",
            )}
          >
            {label}
          </div>
          {info && <InfoPopover title={infoTitle ?? label}>{info}</InfoPopover>}
          {!info && swatchClass && (
            <span className={cn("mt-0.5 h-2.5 w-2.5 shrink-0 rounded-sm", swatchClass)} />
          )}
        </div>
        <div className={cn("text-2xl font-bold tabular-nums", TONE_CLASS[tone])}>
          {value}
        </div>
      </CardHeader>
      {(sub || progress) && (
        <CardContent
          className={cn(
            "pt-0 text-xs text-muted-foreground",
            meter && "flex flex-1 flex-col",
          )}
        >
          {sub}
          {progress && (
            <div className="mt-auto pt-2">
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn("h-full rounded-full", progress.barClass ?? "bg-brand")}
                  style={{ width: `${Math.max(0, Math.min(100, progress.pct))}%` }}
                />
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/** Convenience: pick a tone for a percentage value vs thresholds. */
export function tonePct(
  v: number | null | undefined,
  good: number,
  warn: number,
): KpiTone {
  if (v == null) return "neutral";
  if (v >= good) return "positive";
  if (v >= warn) return "warning";
  return "danger";
}
